import type { PersonProfileCollaborator, PersonProfileSection } from "#types/types.ts";

// Presentation helpers for the About People card. The card itself is the generated `PersonProfile`:
// the `personProfile` SSE event and a conversation GET's `replayEvents` carry the same core DTO.

export type PersonProfileSectionKind =
    | "recentWork"
    | "workingToward"
    | "recognizedFor"
    | "collaborators"
    | "custom";

// Presentation, not payload: Core sends no icon, so these are the names this app maps to its own
// components. "insight" is what a custom section gets, since only its template knows what it is.
export type PersonProfileSectionIcon = "work" | "goal" | "recognition" | "people" | "insight";

// How the subject and a collaborator came to be tied, as the profile page names them. The category
// is shown as a quiet icon rather than a word, so the row stays a name and a face.
export type PersonProfileCollaborationCategory = "communication" | "work" | "engagement";

// Stable, so a collaborator's icons read the same way on every render and between people.
const COLLABORATION_CATEGORY_ORDER: PersonProfileCollaborationCategory[] = ["communication", "work", "engagement"];

// Core constrains each piece of evidence to one of the three category words, so a note is taken at
// its word in any locale, and anything else (a blank note included) claims nothing.
export const presentCollaborationCategories = (person: PersonProfileCollaborator): PersonProfileCollaborationCategory[] => {
    const found = new Set(person.notes.map(note => note.trim().toLowerCase()));

    return COLLABORATION_CATEGORY_ORDER.filter(category => found.has(category));
};

// `titleKey` is the only source for a standard heading — an English literal here would be a second
// one, and the one that reaches a French reader if anything ever rendered it.
export const STANDARD_SECTION_PRESENTATION: Record<
    Exclude<PersonProfileSectionKind, "custom">,
    { icon: PersonProfileSectionIcon; titleKey: `personProfile.sections.${Exclude<PersonProfileSectionKind, "custom">}` }
> = {
    recentWork: { icon: "work", titleKey: "personProfile.sections.recentWork" },
    workingToward: { icon: "goal", titleKey: "personProfile.sections.workingToward" },
    recognizedFor: { icon: "recognition", titleKey: "personProfile.sections.recognizedFor" },
    collaborators: { icon: "people", titleKey: "personProfile.sections.collaborators" }
};

export interface PresentedPersonProfileSection {
    key: string;
    kind: PersonProfileSectionKind;
    // Set for custom sections only; a standard row resolves its heading through `titleKey`.
    title?: string;
    titleKey?: (typeof STANDARD_SECTION_PRESENTATION)[Exclude<PersonProfileSectionKind, "custom">]["titleKey"];
    icon: PersonProfileSectionIcon;
    body?: string | null;
    people: PersonProfileCollaborator[];
}

// The API types `kind` as a plain string, so a kind added there reaches this client and is dropped.
const isStandardSectionKind = (kind: string): kind is Exclude<PersonProfileSectionKind, "custom"> =>
    Object.hasOwn(STANDARD_SECTION_PRESENTATION, kind);

export const presentPersonProfileSections = (sections: readonly PersonProfileSection[]): PresentedPersonProfileSection[] => {
    const presented: PresentedPersonProfileSection[] = [];

    sections.forEach((section, index) => {
        if (section.kind === "custom") {
            const title = section.title?.trim();
            if (!title) {
                return;
            }

            presented.push({
                key: `custom-${index}`,
                kind: "custom",
                title,
                icon: "insight",
                body: section.body,
                people: section.people
            });

            return;
        }

        if (!isStandardSectionKind(section.kind)) {
            return;
        }

        const standard = STANDARD_SECTION_PRESENTATION[section.kind];
        presented.push({
            key: `${index}-${section.kind}`,
            kind: section.kind,
            titleKey: standard.titleKey,
            icon: standard.icon,
            body: section.body,
            people: section.people
        });
    });

    return presented;
};
